import { useCallback, useMemo } from 'react';

import { Select } from '@/lib/base/forms/select/select';
import { getPropertyTypeIconComponent } from '@/lib/crm/utils/icons/PropertyTypeIcon';

import { getEffectiveOptions } from '../booleanOptions';
import type { PropertyInputProps } from './TextInput';

/**
 * Render a `select` (or `boolean`) field as a real picker — not a combobox.
 *
 * A previous version used `Select.ComboBox` which exposed a free-text input,
 * so users could type arbitrary characters into a field whose value space is
 * an enumerated list (e.g. Scheduling Type → Individual / Round Robin /
 * Collective). That was confusing for short, fixed option lists: invalid
 * keystrokes silently produced "no matches" and the field looked like it
 * accepted free input even though only enumerated values are valid. The
 * plain `<Select>` keeps the dropdown affordance, keyboard navigation, and
 * type-to-jump but disallows typing arbitrary text.
 *
 * Note: `inputRef` from PropertyInputProps is intentionally not destructured.
 * The underlying design-system `<Select>` renders a button trigger, not an
 * `<input>` element, and its wrapper does not propagate refs to a focusable
 * node (the prior `Select.ComboBox` version forwarded `ref={inputRef}` but
 * the prop was typed-only — it pointed at a non-input wrapper element, so
 * `inputRef.current?.focus()` was already a no-op for select/boolean fields).
 * If imperative focus on the trigger is needed, add it via the Select
 * component itself rather than this wrapper.
 */
export const SelectInput: React.FC<PropertyInputProps> = ({
  propertyType,
  value,
  onChange,
  disabled = false,
  placeholder,
  autoOpenPicker = false,
  size = 'sm',
}) => {
  const items = useMemo(() => {
    return getEffectiveOptions(propertyType).map(opt => ({
      id: String(opt.value),
      label: opt.label,
    }));
  }, [propertyType]);

  const Icon = getPropertyTypeIconComponent(propertyType);

  const handleSelectionChange = useCallback(
    (key: string | number | null) => {
      onChange(key !== null && key !== undefined ? String(key) : null);
    },
    [onChange]
  );

  const renderItem = useCallback(
    (item: { id: string; label?: string }) => (
      <Select.Item id={item.id}>{item.label}</Select.Item>
    ),
    []
  );

  return (
    <Select
      placeholder={placeholder || `Select ${propertyType.label.toLowerCase()}`}
      // Table-cell editing (autoOpenPicker) renders at the compact `xs`
      // scale so the trigger text matches the table's value type (text-sm),
      // and the option labels are sized down to match — every other host
      // gets the caller's `size` (default `sm`, the roomier standalone
      // form-field treatment). SelectItem renders each option label as
      // `<AriaText slot="label">` (default text-md), so target that slot;
      // `[&_p]` only hits the muted help-text paragraph.
      size={autoOpenPicker ? 'xs' : size}
      popoverClassName={autoOpenPicker ? '[&_[slot=label]]:text-sm' : undefined}
      // Table-cell editing: entering edit mode drops straight into the
      // options instead of requiring a second click on the trigger.
      defaultOpen={autoOpenPicker && !disabled}
      items={items}
      selectedKey={value !== null && value !== undefined ? String(value) : null}
      onSelectionChange={handleSelectionChange}
      isDisabled={disabled}
      aria-label={propertyType.label}
      placeholderIcon={Icon}
    >
      {renderItem}
    </Select>
  );
};
